import ContextMenu from 'src/components/common/ContextMenu';
import * as React from 'react';
import { connect } from 'react-redux';
import { ITableConfig } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import FormButton from 'src/components/controls/forms/redesign/FormButton';
import Filter from 'src/components/common/Filter';
import DialogList from 'src/dialogs';
import Client from 'src/models/Client';
import DeleteConfirmDialog from '../DeleteConfirmDialog';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import actions from 'src/actions';
import { ODataOptions } from 'src/util/ODataBuilder';
import AdminDirectoryLayout from './AdminDirectoryLayout';

interface IClientsPageProps {
    isLoading: boolean;
    clients: PagedResultSet<Client> | undefined;
    tableConfig: ITableConfig;
    getClients: (options: ODataOptions, resetPage?: boolean) => void;
    showDialog: (name: string) => void;
    hideDialog: () => void;
    deleteClient: (clientNumber: number) => void;
}

export class AdminClientsPage extends React.Component<IClientsPageProps> {
    state = { selectedClient: undefined as Client, deleteClientText: '', filterKey: 0, revealedClientId: undefined as number };
    private filterOptions = new ODataOptions();
    private revealTimer: ReturnType<typeof setTimeout>;

    componentWillUnmount() {
        clearTimeout(this.revealTimer);
    }

    handleFilterChanged = (options: ODataOptions) => {
        this.filterOptions = options;
        this.loadPage(1);
    };

    loadPage = (pageNumber: number) => {
        const options = ODataOptions.from({ ...this.props.tableConfig, pageNumber });
        options.merge(this.filterOptions);
        this.props.getClients(options, true);
    };

    showDeleteDialog = (client: Client) => {
        this.setState({ selectedClient: client, deleteClientText: '' });
        this.props.showDialog(DialogList.DELETE_CLIENT);
    };

    deleteClient = (client: Client) => {
        if (client && (client.cloakName || client.name) === this.state.deleteClientText) {
            this.props.deleteClient(client.clientNumber);
            this.props.hideDialog();
        }
    };

    toggleShadow = (client: Client) => {
        clearTimeout(this.revealTimer);
        this.setState({ revealedClientId: this.state.revealedClientId === client.id ? undefined : client.id });
        this.revealTimer = setTimeout(() => this.setState({ revealedClientId: undefined }), 2000);
    };

    render() {
        const { clients, isLoading } = this.props;
        return (
            <AdminDirectoryLayout title="Client Management" subtitle="Search and manage client records.">
                <div className="admin-directory-filter">
                    <Filter
                        key={this.state.filterKey}
                        filterItems={[
                            { group: 1, text: 'Name', orderName: 'name', orderDirection: 'ascending' },
                            { group: 1, text: 'City', orderName: 'city', orderDirection: 'ascending' },
                            { group: 1, text: 'State', orderName: 'state', orderDirection: 'ascending' },
                        ]}
                        onFilterChanged={this.handleFilterChanged}
                        actions={[{ text: 'Clear filters', action: () => this.setState({ filterKey: this.state.filterKey + 1 }) }]}
                    />
                </div>
                <div className="admin-directory-table-wrapper" aria-busy={isLoading}>
                    <table className="admin-directory-table">
                        <thead>
                            <tr>
                                <th scope="col">Name</th>
                                <th scope="col">Address</th>
                                <th scope="col">City</th>
                                <th scope="col">State</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} role="status">
                                        Loading clients...
                                    </td>
                                </tr>
                            ) : clients?.items?.length ? (
                                clients.items.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            {this.state.revealedClientId === c.id ? c.name : c.cloakName || c.name}
                                            {c.isShadow && (
                                                <FormButton
                                                    text=""
                                                    title="Reveal client name"
                                                    size="small"
                                                    onClick={() => this.toggleShadow(c)}
                                                    icon="shadow"
                                                />
                                            )}
                                        </td>
                                        <td>{c.address?.addressLine1}</td>
                                        <td>{c.address?.city}</td>
                                        <td>{c.address?.state?.abbreviation}</td>
                                        <td>
                                            <div className="admin-directory-actions">
                                                <ContextMenu
                                                    isVisible={true}
                                                    actions={[{ text: 'Delete Client', action: () => this.showDeleteDialog(c) }]}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} role="status">
                                        {this.filterOptions.getFilters().length ? 'No clients match your filters.' : 'No clients available.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {clients && clients.totalRecords > 0 && (
                    <div className="admin-directory-footer">
                        <span>
                            Page {clients.pageNumber} of {Math.ceil(clients.totalRecords / clients.pageSize)}
                        </span>
                        <div className="admin-directory-actions">
                            <FormButton
                                text="Previous"
                                disabled={isLoading || clients.pageNumber <= 1}
                                onClick={() => this.loadPage(clients.pageNumber - 1)}
                            />
                            <FormButton
                                text="Next"
                                disabled={isLoading || clients.pageNumber * clients.pageSize >= clients.totalRecords}
                                onClick={() => this.loadPage(clients.pageNumber + 1)}
                            />
                        </div>
                    </div>
                )}
                <DeleteConfirmDialog
                    key={this.state.selectedClient?.id}
                    title="Delete Client"
                    promptText="Type Client Name to confirm delete"
                    dialogName={DialogList.DELETE_CLIENT}
                    delete={this.deleteClient}
                    item={this.state.selectedClient}
                    updateText={(deleteClientText) => this.setState({ deleteClientText })}
                />
            </AdminDirectoryLayout>
        );
    }
}

const mapStateToProps = (state: IRootState) => ({
    isLoading: selectors.client.isLoadingClients(state),
    clients: selectors.client.clients(state),
    tableConfig: selectors.client.clientsPageTableConfig(state),
});

export default connect(mapStateToProps, {
    getClients: actions.client.getClients,
    showDialog: actions.layout.showDialog,
    hideDialog: actions.layout.hideDialog,
    deleteClient: actions.client.deleteClient,
})(AdminClientsPage);



// WEBPACK FOOTER //
// ./src/components/admin/sections/AdminClientsPage.tsx